import type { MessageKey } from '@dijitalapp/i18n';
import type { AdminUsernameRuleDto, UsernameRule, UsernameVerdict } from '@dijitalapp/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';

type Match = UsernameRule['match'];
type Kind = UsernameRule['kind'];
const MATCHES: Match[] = ['word', 'whole', 'contains'];
const KINDS: Kind[] = ['reserved', 'blocked'];

/**
 * Admin: which usernames may be taken. A tester, the admin's own terms (add/remove) and the
 * built-in lists for reference. Reserved names stay open to staff; blocked names to nobody.
 */
export function UsernameRulesTab() {
  const { i18n } = useApp();
  const { t, formatDate } = i18n;
  const [rules, setRules] = useState<AdminUsernameRuleDto[]>([]);
  const [builtin, setBuiltin] = useState<readonly UsernameRule[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<{ term: string; match: Match; kind: Kind; note: string }>({ term: '', match: 'word', kind: 'reserved', note: '' });
  const [probe, setProbe] = useState('');
  const [result, setResult] = useState<{ username: string; valid: boolean; verdict: UsernameVerdict | null } | null>(null);

  const load = () =>
    api<{ rules: AdminUsernameRuleDto[]; builtin: UsernameRule[] }>('GET', '/admin/username-rules').then((r) => {
      setRules(r.rules);
      setBuiltin(r.builtin);
    });
  useEffect(() => {
    void api<{ rules: AdminUsernameRuleDto[]; builtin: UsernameRule[] }>('GET', '/admin/username-rules').then((r) => {
      setRules(r.rules);
      setBuiltin(r.builtin);
    });
  }, []);

  const run = (fn: () => Promise<unknown>) => {
    setError(null);
    fn().catch((err: unknown) => setError(t(errorMessageKey(err))));
  };
  const add = (e: FormEvent) => {
    e.preventDefault();
    run(async () => {
      await api('POST', '/admin/username-rules', form);
      setForm((f) => ({ ...f, term: '', note: '' }));
      await load();
    });
  };
  const test = (e: FormEvent) => {
    e.preventDefault();
    run(async () => setResult(await api('POST', '/admin/username-rules/test', { username: probe })));
  };

  const verdictText = (r: NonNullable<typeof result>) => {
    if (!r.valid) return t('adminOps.uname.invalid');
    const v = r.verdict;
    if (!v || v.ok) return t('adminOps.uname.allowed', { name: r.username });
    return t(`adminOps.uname.refused.${v.kind}` as MessageKey, {
      term: v.term,
      source: t(v.source === 'builtin' ? 'adminOps.uname.sourceBuiltin' : 'adminOps.uname.sourceCustom'),
    });
  };

  return (
    <section className="stack">
      <p className="muted">{t('adminOps.uname.intro')}</p>

      <form className="card stack" onSubmit={test}>
        <h2 className="card-title">{t('adminOps.uname.testTitle')}</h2>
        <div className="row">
          <input
            className="input"
            style={{ flex: 1, minWidth: 180 }}
            aria-label={t('adminOps.uname.testTitle')}
            placeholder={t('adminOps.uname.testPlaceholder')}
            autoCapitalize="none"
            spellCheck={false}
            value={probe}
            onChange={(e) => setProbe(e.target.value)}
          />
          <button className="btn" disabled={!probe.trim()}>
            {t('adminOps.uname.test')}
          </button>
        </div>
        {result && (
          <p className={`username-check ${result.valid && result.verdict?.ok ? 'is-ok' : 'is-bad'}`} role="status">
            {verdictText(result)}
          </p>
        )}
      </form>

      <form className="card stack" onSubmit={add}>
        <h2 className="card-title">{t('adminOps.uname.addTitle')}</h2>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: 1, minWidth: 160 }}>
            <label htmlFor="ur-term">{t('adminOps.uname.term')}</label>
            <input id="ur-term" className="input" required minLength={2} maxLength={40} value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="ur-match">{t('adminOps.uname.match')}</label>
            <select id="ur-match" className="input" value={form.match} onChange={(e) => setForm({ ...form, match: e.target.value as Match })}>
              {MATCHES.map((m) => (
                <option key={m} value={m}>
                  {t(`adminOps.uname.match.${m}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="ur-kind">{t('adminOps.uname.kind')}</label>
            <select id="ur-kind" className="input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as Kind })}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`adminOps.uname.kind.${k}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="ur-note">{t('adminOps.uname.note')}</label>
          <input id="ur-note" className="input" maxLength={200} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </div>
        <p className="hint">{t(`adminOps.uname.matchHint.${form.match}` as MessageKey)}</p>
        <div className="row">
          <span className="spacer" />
          <button className="btn btn-primary">{t('adminOps.uname.add')}</button>
        </div>
      </form>
      {error && (
        <p className="alert alert-error" role="alert">
          {error}
        </p>
      )}

      <div className="card stack">
        <h2 className="card-title">{t('adminOps.uname.customTitle', { count: rules.length })}</h2>
        {rules.length === 0 && <p className="muted">{t('adminOps.uname.none')}</p>}
        {rules.map((r) => (
          <div key={r.id} className="toggle-row">
            <div style={{ minWidth: 0 }}>
              <strong>{r.term}</strong>{' '}
              <span className={`badge ${r.kind === 'blocked' ? 'badge-danger' : ''}`}>{t(`adminOps.uname.kind.${r.kind}` as MessageKey)}</span>{' '}
              <span className="badge">{t(`adminOps.uname.match.${r.match}` as MessageKey)}</span>
              <p className="muted">
                {r.note ? `${r.note} · ` : ''}
                {r.createdBy ? `@${r.createdBy} · ` : ''}
                {formatDate(r.createdAt, { dateStyle: 'medium' })}
              </p>
            </div>
            <span className="spacer" />
            <button
              className="btn btn-sm btn-danger"
              type="button"
              onClick={() =>
                run(async () => {
                  await api('DELETE', `/admin/username-rules/${r.id}`);
                  await load();
                })
              }
            >
              {t('adminOps.uname.remove')}
            </button>
          </div>
        ))}
      </div>

      <div className="card stack">
        <h2 className="card-title">{t('adminOps.uname.builtinTitle')}</h2>
        <p className="muted">{t('adminOps.uname.builtinHint')}</p>
        {KINDS.map((kind) => (
          <details key={kind} className="term-group">
            <summary>
              {t(`adminOps.uname.kind.${kind}` as MessageKey)} · {builtin.filter((r) => r.kind === kind).length}
            </summary>
            {MATCHES.map((match) => {
              const terms = builtin.filter((r) => r.kind === kind && r.match === match);
              if (terms.length === 0) return null;
              return (
                <div key={match} className="stack" style={{ gap: 'var(--space-1)' }}>
                  <h3 className="subhead">{t(`adminOps.uname.match.${match}` as MessageKey)}</h3>
                  <div className="term-chips">
                    {terms.map((r) => (
                      <span key={r.term} className="term-chip">
                        {r.term}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </details>
        ))}
      </div>
    </section>
  );
}
